import './services.pnotify';
import './services.cart';
import './services.logger';
import './service.promotion';
import './services.tracker';
import dayjs from 'dayjs';
import { COUPON_ERROR, DISCOUNT_ON } from '../constants/promotion';
import { hasFeature } from '../../utils/featureService';
import { EVENT_NAME_FOR_COUPON_CENTER } from '../constants/tracker';

const CLAIM_RESULT = {
  succeed: 'succeed',
  failed: 'failed',
};

const CLAIM_COUPON_BY_ID_I18N = {
  succeed: 'coupon.congratulation',
  // failed
  [COUPON_ERROR.not_login]: 'coupon.login_or_register',
  [COUPON_ERROR.fulled]: 'coupon.usage_limit',
  [COUPON_ERROR.expired]: 'coupon.is_expired',
  [COUPON_ERROR.not_eligible]: 'coupon.not_eligible',
  [COUPON_ERROR.already_taken]: 'coupon.already_taken',
  [COUPON_ERROR.unfit_first_purchase]: 'coupon.unfit_first_purchase',
};

app.controller('CouponCenterController', [
  '$scope',
  '$window',
  'mainConfig',
  'promotionService',
  'staticResourceHost',
  'logger',
  'cartService',
  'pnotifyService',
  '$filter',
  'trackerService',
  function (
    $scope,
    $window,
    mainConfig,
    promotionService,
    staticResourceHost,
    logger,
    cartService,
    pnotifyService,
    $filter,
    trackerService,
  ) {
    $scope.staticResourceHost = staticResourceHost;
    $scope.coupons = [];
    $scope.isLoading = false;
    $scope.couponCenterState = {
      showBackToCart: false,
      cartItemCount: 0,
    };

    const isCouponStarted = (startAt) =>
      startAt === null || dayjs(startAt).isBefore(dayjs());

    const getCoupons = () => {
      $scope.isLoading = true;

      promotionService
        .getCouponCenterCoupons()
        .then((res) => {
          $scope.coupons = res.data.data.items;
        })
        .catch((error) => {
          logger.error('failed to getCoupons in coupon center', error);
        })
        .finally(() => {
          $scope.isLoading = false;
          const hasAvailableCoupon = $scope.coupons.some(
            (coupon) => coupon.is_drew === false,
          );
          trackerService.track({
            type: EVENT_NAME_FOR_COUPON_CENTER.COUPON_IMPRESSION,
            data: {
              scope: 'shop',
              event_category: 'CouponCenter',
              coupon_available: hasAvailableCoupon
                ? 'available_true'
                : 'available_false',
            },
          });
        });
    };
    getCoupons();

    $scope.handleClaimByCodeSucceed = ({ name }) => {
      trackerService.track({
        type: EVENT_NAME_FOR_COUPON_CENTER.COUPON_CODE_REDEEM,
        data: {
          scope: 'shop',
          event_category: 'CouponCenter',
          collect_location: 'coupon_center',
        },
      });

      pnotifyService.notify(
        $filter('translate')('coupon_input.claim_succeed.redirect', {
          couponName: name,
        }),
        { customClass: 'success', icon: 'fa fa-check-circle' },
        () => {
          window.location.href = '/coupons';
        },
      );
    };

    const setCouponClaimed = (targetCoupon) => {
      $scope.coupons = $scope.coupons.map((coupon) => {
        if (coupon._id === targetCoupon._id) {
          return { ...coupon, is_drew: true };
        }
        return coupon;
      });
    };

    const updateCount = (count = 0) => {
      $scope.couponCenterState.cartItemCount = count;
      $scope.couponCenterState.showBackToCart =
        count > 0 && hasFeature('coupon_center_back_to_cart');
    };

    $scope.$on('cartItemsUpdated', function (_, cartData) {
      updateCount(cartData?.items?.length ?? 0);
    });

    $scope.$on('cartItemCountUpdated', function (_, count) {
      updateCount(count);
    });

    cartService.getItemCount();

    $scope.onCouponActionClick = (coupon) => {
      if (coupon.is_drew && hasFeature('coupon_center_back_to_cart')) {
        $scope.applyCouponClick(coupon);
        return;
      }
      $scope.claimCouponById(coupon);
    };

    $scope.applyCouponClick = (coupon) => {
      trackerService.track({
        type: EVENT_NAME_FOR_COUPON_CENTER.COUPON_USE_NOW_CLICK,
        data: { event_category: 'CouponCenter' },
      });

      const isProductOrCategoryPromotion = [
        DISCOUNT_ON.item,
        DISCOUNT_ON.category,
      ].includes(coupon.discount_on);

      const isCartPage =
        $scope.couponCenterState.cartItemCount > 0 &&
        isCouponStarted(coupon.start_at) &&
        !isProductOrCategoryPromotion;

      if (isCartPage) {
        window.location = '/cart';
        return;
      }

      if (isProductOrCategoryPromotion && coupon.seo_enabled) {
        window.location = `/promotions/${coupon._id}`;
        return;
      }

      window.location = '/';
    };

    $scope.getActionText = (coupon) => {
      if ($scope.getCouponApplicable(coupon)) {
        return $filter('translate')('coupon_v2.use');
      }
      return coupon.is_drew
        ? $filter('translate')('coupon_v2.claimed')
        : $filter('translate')('coupon_v2.claim');
    };

    $scope.getCouponApplicable = (coupon) =>
      coupon.is_drew &&
      hasFeature('coupon_center_back_to_cart') &&
      coupon.available_platforms.includes('ec');

    $scope.claimCouponById = (coupon) => {
      promotionService
        .claimCouponById(coupon._id)
        .then((res) => {
          const { result, failed_status } = res.data;

          let status;
          if (result === CLAIM_RESULT.succeed) {
            status = 'success';
            setCouponClaimed(coupon);
            pnotifyService.notify(
              $filter('translate')(CLAIM_COUPON_BY_ID_I18N.succeed),
              { customClass: 'success', icon: 'fa fa-check-circle' },
            );
          } else {
            status = failed_status;
            pnotifyService.notify(
              $filter('translate')(CLAIM_COUPON_BY_ID_I18N[failed_status]),
              {
                customClass: 'error',
                icon: 'fa fa-exclamation-triangle',
              },
            );
          }

          trackerService.track({
            type: EVENT_NAME_FOR_COUPON_CENTER.COLLECT_COUPON_CLICK,
            data: {
              scope: 'shop',
              event_category: 'CouponCenter',
              discount_type: coupon.discount_type,
              available_platform: coupon.available_platforms,
              collect_coupon_result: status,
            },
          });
        })
        .catch((error) => {
          logger.error('Error when claiming coupon by id', error);
          pnotifyService.notify(
            $filter('translate')('coupon_input.claim_failed'),
            {
              customClass: 'error',
              icon: 'fa fa-exclamation-triangle',
            },
          );
        });
    };

    $scope.showBackToCart = () =>
      $scope.couponCenterState.cartItemCount > 0 &&
      hasFeature('coupon_center_back_to_cart');

    $scope.onBackToCartClick = () => {
      trackerService.track({
        type: EVENT_NAME_FOR_COUPON_CENTER.COUPON_CENTER_GO_TO_CART_CLICK,
        data: { event_category: 'CouponCenter' },
      });
      $window.location = mainConfig.checkoutLandingPath;
    };

    $scope.$watch('couponCenterState.showBackToCart', (nextState, preState) => {
      if (!preState && nextState) {
        trackerService.track({
          type: EVENT_NAME_FOR_COUPON_CENTER.COUPON_CENTER_GO_TO_CART_SHOW,
          data: { event_category: 'CouponCenter' },
        });
      }
    });
  },
]);
